import { Injectable, Renderer2, RendererFactory2, DOCUMENT } from '@angular/core';

import { Inject } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {
  private renderer: Renderer2;

  constructor(
    private rendererFactory: RendererFactory2,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  loadScript(src: string, id: string, async: boolean = true, defer: boolean = false): Promise<Event> {
    return new Promise((resolve, reject) => {
      if (this.document.getElementById(id)) {
        console.warn(`Script with ID ${id} already loaded.`);
        resolve(new Event('alreadyloaded'));
        return;
      }

      const script = this.renderer.createElement('script');
      script.id = id;
      script.src = src;
      script.async = async;
      script.defer = defer;

      script.onload = resolve;
      script.onerror = reject;

      this.renderer.appendChild(this.document.head, script);
    });
  }

  loadInlineScript(scriptContent: string, id: string): Promise<Event> {
    return new Promise((resolve, reject) => {
      if (this.document.getElementById(id)) {
        console.warn(`Inline script with ID ${id} already loaded.`);
        resolve(new Event('alreadyloaded'));
        return;
      }

      const script = this.renderer.createElement('script');
      script.id = id;
      script.type = 'text/javascript';
      script.textContent = scriptContent;

      script.onload = resolve;
      script.onerror = reject;

      this.renderer.appendChild(this.document.head, script);
    });
  }

  loadGTM(dataLayerId: string): Promise<Event> {
    const gtmScriptContent = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${dataLayerId}');
    `;
    return this.loadInlineScript(gtmScriptContent, 'gtm-script');
  }

  loadHotjar(hjid: number, hjsv: number): Promise<Event> {
    const hotjarScriptContent = `
      (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:${hjid},hjsv:${hjsv}};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.defer=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `;
    return this.loadInlineScript(hotjarScriptContent, 'hotjar-script');
  }

  loadGoogleAnalytics(gaId: string): Promise<Event> {
    return this.loadScript(`https://www.googletagmanager.com/gtag/js?id=${gaId}`, 'google-analytics-gtag', true, false)
      .then(() => {
        const gaConfigScriptContent = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', '${gaId}');
        `;
        return this.loadInlineScript(gaConfigScriptContent, 'google-analytics-config');
      });
  }

  loadGoogleAds(adId: string): Promise<Event> {
    return this.loadScript(`https://www.googletagmanager.com/gtag/js?id=${adId}`, 'google-ads-gtag', true, false)
      .then(() => {
        const adConfigScriptContent = `
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}\n
          gtag('js', new Date());
          gtag('config', '${adId}');
        `;
        return this.loadInlineScript(adConfigScriptContent, 'google-ads-config');
      });
  }

  loadGoogleMaps(apiKey: string): Promise<Event> {
    const googleMapsScriptContent = `
      (g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src=\`https://maps.\${c}apis.com/maps/api/js?\`+e;a.defer=true;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}))();d[l]?console.warn(p+" only loads once. Ignoring:",g):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
        v: "weekly",
        key: '${apiKey}'
      });
    `;
    return this.loadInlineScript(googleMapsScriptContent, 'google-maps-script');
  }
}
